import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Stack,
  CircularProgress,
  IconButton,
  Alert,
  LinearProgress,
  Divider,
  Paper,
  Chip,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  AttachMoney as MoneyIcon,
  Image as ImageIcon,
  AutoFixHigh as AutoIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { paymentAPI } from '../services/api';
import Tesseract from 'tesseract.js';

const PaymentModal = ({ open, onClose, plan, courses = [], paymentMethods = [], onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    transactionId: '',
    screenshot: null,
    service: 'other', // kpay, wavepay, mytelpay, other
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [candidates, setCandidates] = useState([]); // OCR candidates
  const [copied, setCopied] = useState(null);

  const SERVICES = [
    { value: 'other', label: 'Other' },
    { value: 'kpay', label: 'KBZPay' },
    { value: 'wavepay', label: 'WavePay' },
    { value: 'mytelpay', label: 'MyTelPay' },
  ];

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(text);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const performOCR = async (file) => {
    if (!file) return;

    try {
      setOcrLoading(true);
      const result = await Tesseract.recognize(file, 'eng');
      const text = result.data.text.toLowerCase();
      
      console.log('OCR Text:', text);

      // Service Detection
      let detectedService = 'other';
      if (text.includes('kbz')) detectedService = 'kpay';
      else if (text.includes('wave')) detectedService = 'wavepay';
      else if (text.includes('mytel')) detectedService = 'mytelpay';

      // Candidate Extraction (9+ digits)
      const matches = text.match(/\b\d{9,}\b/g) || [];
      // Unique matches only
      const uniqueCandidates = [...new Set(matches)];
      setCandidates(uniqueCandidates);

      console.log('Detected Service:', detectedService);
      console.log('Candidates:', uniqueCandidates);

      setFormData((prev) => {
        let newTid = prev.transactionId;
        
        // Auto-fill logic
        if (uniqueCandidates.length === 1) {
          newTid = uniqueCandidates[0];
        } else if (uniqueCandidates.length === 0) {
          // If 0 candidates, do not change existing ID (or keep empty)
        } 
        // If 2+ candidates, we wait for user input (handled in handleIdChange)

        return { 
          ...prev, 
          service: detectedService,
          transactionId: newTid
        };
      });

    } catch (err) {
      console.error('OCR Error:', err);
    } finally {
      setOcrLoading(false);
    }
  };

  const handleIdChange = (e) => {
    const value = e.target.value;
    
    // Check if user input matches exactly one candidate prefix (if we have multiple candidates)
    if (candidates.length > 1) {
      const matching = candidates.filter(c => c.startsWith(value));
      if (matching.length === 1) {
        // Auto-fill the rest
        setFormData((prev) => ({ ...prev, transactionId: matching[0] }));
        setCandidates([]); // Clear candidates to stop watcher
        return;
      }
    }

    setFormData((prev) => ({ ...prev, transactionId: value }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, screenshot: file }));
      setPreviewUrl(URL.createObjectURL(file));
      performOCR(file);
    }
  };

  const handleSubmit = async () => {
    if (!formData.transactionId || !formData.screenshot) {
      setError('Please provide both Transaction ID and Screenshot');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const normalizedTier = typeof plan?.tier === 'string' ? plan.tier.toLowerCase() : '';
      const packagePlan =
        normalizedTier === 'diamond' || normalizedTier === 'gold' || normalizedTier === 'silver'
          ? normalizedTier
          : 'individual';

      const majorValue = typeof plan?.major === 'string' ? plan.major.trim() : '';
      if (!majorValue) {
        setError('Missing major for this payment');
        return;
      }

      const resolvedCourses =
        Array.isArray(courses) && courses.length > 0
          ? courses
          : Array.isArray(plan?.courses) && plan.courses.length > 0
            ? plan.courses
            : [];

      if (resolvedCourses.length === 0) {
        setError('Please select at least one course for this payment');
        return;
      }

      const data = new FormData();
      // Prefix transaction ID with service
      const prefix = formData.service === 'other' ? 'other_' : `${formData.service}_`;
      const finalTid = `${prefix}${formData.transactionId.trim()}`;
      
      data.append('transactionId', finalTid);
      
      data.append('amount', plan.price);
      data.append('major', majorValue);
      data.append('package_plan', packagePlan);
      
      // If it's a bundle, we might want to send course IDs if needed, 
      // but usually the plan ID is enough on backend.
      // If individual courses are passed (custom bundle), add them
      resolvedCourses.forEach((courseId) => {
        data.append('courses[]', courseId);
      });

      data.append('screenshot', formData.screenshot);

      // Debug: Log FormData contents
      console.log('Submitting payment:', {
        transactionId: finalTid,
        amount: plan.price,
        major: majorValue,
        package_plan: packagePlan,
        courses: resolvedCourses,
        screenshot: formData.screenshot.name
      });

      await paymentAPI.submitPayment(data);
      
      onSuccess?.();
      handleClose();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Payment submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ transactionId: '', screenshot: null });
    setPreviewUrl(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={!loading ? handleClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          Confirm Payment
        </Typography>
        {!loading && (
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ py: 3 }}>
        <Stack spacing={3}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* Plan Summary */}
          <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Payment for:
            </Typography>
            <Typography variant="subtitle1" fontWeight={700}>
              {plan?.name}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
              <MoneyIcon fontSize="small" color="primary" />
              <Typography variant="h6" color="primary.main" fontWeight={800}>
                {plan?.priceLabel || `${plan?.price} MMK`}
              </Typography>
            </Stack>
          </Box>

          {/* Payment Methods */}
          {paymentMethods.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Payment Methods
              </Typography>
              <Stack spacing={1.5}>
                {paymentMethods.map((method, index) => (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderRadius: 2,
                      bgcolor: 'background.default',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar 
                        src={method.logo}
                        variant="rounded"
                        sx={{ width: 32, height: 32, bgcolor: 'transparent', objectFit: 'contain' }}
                      >
                        {!method.logo && <MoneyIcon fontSize="small" />}
                      </Avatar>
                      <Box>
                        <Typography variant="caption" fontWeight={700} display="block">
                          {method.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {method.accountName}
                        </Typography>
                      </Box>
                    </Stack>
                    
                    <Tooltip title={copied === method.accountNumber ? "Copied!" : "Copy Number"}>
                      <Button
                        size="small"
                        variant={copied === method.accountNumber ? "contained" : "outlined"}
                        color={copied === method.accountNumber ? "success" : "primary"}
                        onClick={() => handleCopy(method.accountNumber)}
                        startIcon={copied === method.accountNumber ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                        sx={{ 
                          minWidth: 0, 
                          px: 1.5, 
                          py: 0.5, 
                          fontSize: '0.75rem',
                          textTransform: 'none',
                          borderRadius: 2
                        }}
                      >
                        {method.accountNumber}
                      </Button>
                    </Tooltip>
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}

          {/* Screenshot Upload */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Payment Screenshot *
            </Typography>
            
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="screenshot-upload"
              type="file"
              onChange={handleFileChange}
              disabled={loading || ocrLoading}
            />
            
            <label htmlFor="screenshot-upload">
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: formData.screenshot ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: loading ? 'default' : 'pointer',
                  bgcolor: formData.screenshot ? 'primary.lighter' : 'transparent',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                  }
                }}
              >
                {previewUrl ? (
                  <Box sx={{ position: 'relative' }}>
                    <img 
                      src={previewUrl} 
                      alt="Preview" 
                      style={{ maxHeight: 200, maxWidth: '100%', borderRadius: 8 }} 
                    />
                    <Button 
                      size="small" 
                      sx={{ mt: 1, display: 'block', mx: 'auto' }}
                      startIcon={<UploadIcon />}
                    >
                      Change Image
                    </Button>
                  </Box>
                ) : (
                  <Stack alignItems="center" spacing={1} color="text.secondary">
                    <ImageIcon sx={{ fontSize: 40, color: 'action.active' }} />
                    <Typography variant="body2">
                      Tap to upload screenshot
                    </Typography>
                  </Stack>
                )}
              </Box>
            </label>
          </Box>

          {/* Transaction ID Input */}
          <TextField
            label="Transaction ID"
            fullWidth
            required
            value={formData.transactionId}
            onChange={handleIdChange}
            disabled={loading || ocrLoading}
            placeholder="e.g., 1234567890"
            helperText={ocrLoading ? "Scanning image for transaction ID..." : "Upload screenshot to auto-fill"}
            InputProps={{
              endAdornment: ocrLoading && <CircularProgress size={20} />
            }}
          />

          {/* Service Selector */}
          <TextField
            select
            label="Payment Service"
            fullWidth
            value={formData.service}
            onChange={(e) => setFormData({ ...formData, service: e.target.value })}
            disabled={loading || ocrLoading}
            SelectProps={{ native: true }}
          >
            {SERVICES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </TextField>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !formData.transactionId || !formData.screenshot}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {loading ? 'Submitting...' : 'Submit Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentModal;
